// メンバー画面（portal.md「ユーザー管理」「招待管理」「チーム管理」を 1 画面にまとめる）。admin 以上。
// 左にチーム一覧（絞り込み・作成）、本体にメンバー一覧（招待中を含む）。招待メールの送信とチームへの追加・除外は
// 一覧で選んでまとめて行う（権限・状態の変更、登録の取り消し・除外も同じ）。
// ユーザー追加・サービス可否の編集・チーム編集はポップアップで開く。

import { useEffect, useState } from "react";
import { api, messageFor, type Role, type Team, type ContractRows, type UserRow } from "../lib/api";
import { displayStatus } from "../lib/status";
import { AddUserPanel } from "./AddUserPanel";
import { TeamEditor } from "./TeamEditor";
import { ChoicePicker, Dropdown, TeamPicker } from "./BulkActions";
import { ServiceEditor } from "./ServiceEditor";
import { Modal, SubCode, fmt, roleLabel } from "./shared";
import { useContract } from "./ContractLayout";

type Dialog =
  | { kind: "add" }
  | { kind: "user"; user: UserRow }
  | { kind: "team"; team: Team | null };

export function MembersPage() {
  const { contractId } = useContract();
  const [rows, setRows] = useState<ContractRows | null>(null);
  const [members, setMembers] = useState<UserRow[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teamQuery, setTeamQuery] = useState("");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  const [menu, setMenu] = useState<"team" | "role" | "status" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    try {
      const [t, u] = await Promise.all([api.contract(contractId), api.users(contractId)]);
      setRows(t); setMembers(u.users);
    } catch (e) { setError(messageFor(e)); }
  };
  useEffect(() => { void load(); }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows) return <main>{error ? <p className="error">{error}</p> : <p className="muted">読み込み中…</p>}</main>;

  const countOf = (id: string) => members.filter((u) => u.teams.some((t) => t.teamId === id)).length;
  const team = rows.teams.find((t) => t.teamId === teamId) ?? null;
  const shown = team ? members.filter((u) => u.teams.some((t) => t.teamId === team.teamId)) : members;
  const q = teamQuery.trim().toLowerCase();
  const teams = q ? rows.teams.filter((t) => t.name.toLowerCase().includes(q)) : rows.teams;
  // 一括操作の対象は表示中の行で選んだ人だけ（チームを切り替えると選択は外す）。
  const picked = shown.filter((u) => selected.has(u.sub));
  // 招待メールを送れるのは invited（ログイン前(招待未送信)・ログイン前(招待済)・受諾済み・期限切れ）だけ。
  const invitees = picked.filter((u) => u.status === "invited");
  const selectTeam = (id: string | null) => { setTeamId(id); setSelected(new Set()); };
  const toggle = (sub: string, on: boolean) => setSelected((cur) => {
    const next = new Set(cur);
    if (on) next.add(sub); else next.delete(sub);
    return next;
  });
  const toggleAll = (on: boolean) => setSelected(on ? new Set(shown.map((u) => u.sub)) : new Set());

  // 選んだ人に 1 人ずつ同じ操作をし、失敗した人は理由を添えて選んだまま残す（もう一度押せばその人だけやり直せる）。
  const runBulk = async (users: UserRow[], verb: string, done: (n: number) => string, fn: (u: UserRow) => Promise<unknown>) => {
    setError(null); setNotice(null);
    const failed: { u: UserRow; message: string }[] = [];
    for (const [i, u] of users.entries()) {
      setProgress(`${verb}… ${i + 1} / ${users.length}`);
      try { await fn(u); } catch (e) { failed.push({ u, message: messageFor(e) }); }
    }
    setProgress(null);
    setSelected(new Set(failed.map((f) => f.u.sub)));
    const ok = users.length - failed.length;
    if (ok > 0) setNotice(done(ok));
    if (failed.length) setError(`処理できなかったユーザーがいます。\n${failed.map((f) => `${f.u.email}: ${f.message}`).join("\n")}`);
    await load();
  };

  // 途中で本人がログインして active になった人は already_member で失敗する。
  const sendInvitations = () => {
    const resent = invitees.filter((u) => u.invitedAt).length;
    const note = resent > 0 ? `\nうち ${resent} 人は再送です。前のメールのリンクと仮パスワードは使えなくなります。` : "";
    if (!confirm(`${invitees.length} 人に招待メールを送ります。よろしいですか？${note}`)) return;
    void runBulk(invitees, "送信中", (n) => `${n} 人に招待メールを送りました。`, (u) => api.sendInvitation(contractId, u.sub));
  };

  // 招待中の人だけが対象。他の契約に所属が無く一度もログインしていなければ共有プールのユーザーも消える。
  const cancelRegistrations = () => {
    if (!confirm(`${invitees.length} 人の登録を取り消します。他の契約に所属が無く一度もログインしていない人は、共有プールのユーザーも削除します。よろしいですか？`)) return;
    void runBulk(invitees, "取り消し中", (n) => `${n} 人の登録を取り消しました。`, (u) => api.cancelInvitation(contractId, u.sub));
  };

  // 招待中でない人（ログイン済み・無効）が対象。本人性と他の契約の所属は残る。最後の管理者は last_admin。
  const removable = picked.filter((u) => u.status !== "invited");
  const removeFromContract = () => {
    if (!confirm(`${removable.length} 人をこの契約から除外します。よろしいですか？`)) return;
    void runBulk(removable, "除外中", (n) => `${n} 人をこの契約から除外しました。`, (u) => api.removeUser(contractId, u.sub));
  };

  // チームごとに「全員を入れる（on）」「全員から外す（off）」を適用する。所属が変わる人だけを更新する。
  const applyTeams = (change: Record<string, "on" | "off">) => {
    const users = picked
      .map((u) => {
        const before = u.teams.map((t) => t.teamId);
        const after = rows.teams.map((t) => t.teamId).filter((id) => (change[id] ? change[id] === "on" : before.includes(id)));
        return { u, after, changed: after.length !== before.length || after.some((id) => !before.includes(id)) };
      })
      .filter((x) => x.changed);
    if (users.length === 0) { setError(null); setNotice("所属チームが変わる人はいません。"); return; }
    void runBulk(users.map((x) => x.u), "変更中", (n) => `${n} 人の所属チームを変更しました。`,
      (u) => api.updateUser(contractId, u.sub, { teams: users.find((x) => x.u.sub === u.sub)!.after }));
  };

  // 最後の管理者を降格する人は last_admin で失敗し、選んだまま残る。
  const changeRole = (role: Role) => {
    const users = picked.filter((u) => u.role !== role);
    if (users.length === 0) { setError(null); setNotice(`選んだ人はすべて${roleLabel(role)}です。`); return; }
    if (!confirm(`${users.length} 人の権限を${roleLabel(role)}にします。よろしいですか？`)) return;
    void runBulk(users, "変更中", (n) => `${n} 人の権限を${roleLabel(role)}にしました。`,
      (u) => api.updateUser(contractId, u.sub, { role }));
  };

  // invited は状態を変えられない（有効化はログイン時のトリガー）ので対象から外す。最後の管理者の無効化は last_admin。
  const statusLabel = (v: "active" | "disabled") => (v === "active" ? "有効" : "無効");
  const changeStatus = (status: "active" | "disabled") => {
    const users = picked.filter((u) => u.status !== "invited" && u.status !== status);
    if (users.length === 0) { setError(null); setNotice(`状態を${statusLabel(status)}にできる人がいません（招待中の人は変えられません）。`); return; }
    const note = status === "disabled" ? "\nこの契約の全サービスが使えなくなります。" : "";
    if (!confirm(`${users.length} 人を${statusLabel(status)}にします。よろしいですか？${note}`)) return;
    void runBulk(users, "変更中", (n) => `${n} 人を${statusLabel(status)}にしました。`,
      (u) => api.updateUser(contractId, u.sub, { status }));
  };

  // プルダウンで値を選んで適用したら閉じる。
  const applyAndClose = <T,>(fn: (v: T) => void) => (v: T) => { setMenu(null); fn(v); };
  const toggleMenu = (m: NonNullable<typeof menu>) => (on: boolean) => setMenu(on ? m : null);

  const open = (d: Dialog) => { setDialogError(null); setDialog(d); };
  const close = () => setDialog(null);
  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setDialogError(null);
    try { await fn(); close(); after?.(); await load(); } catch (e) { setDialogError(messageFor(e)); }
  };

  return (
    <div className="members">
      <aside className="members-side">
        <button type="button" className={`side-item all${teamId === null ? " active" : ""}`} onClick={() => selectTeam(null)}>
          全メンバー ({members.length})
        </button>
        <div className="side-head">
          <span className="muted">チーム</span>
          <span className="spacer" />
          <button type="button" className="link" onClick={() => open({ kind: "team", team: null })}>＋ チームを作成</button>
        </div>
        <input type="text" placeholder="チームを絞り込む" value={teamQuery} onChange={(e) => setTeamQuery(e.target.value)} />
        {rows.teams.length === 0 && <p className="muted">チームはまだありません。</p>}
        {teams.map((t) => (
          <button key={t.teamId} type="button" className={`side-item${teamId === t.teamId ? " active" : ""}`} onClick={() => selectTeam(t.teamId)}>
            <span className="avatar team" aria-hidden>{t.name.slice(0, 1)}</span>
            {t.name} ({countOf(t.teamId)})
          </button>
        ))}
      </aside>

      <section className="members-main">
        <div className="row">
          <h1>{team ? team.name : "全メンバー"} ({shown.length})</h1>
          {team && <button type="button" className="link title-link" onClick={() => open({ kind: "team", team })}>チームを編集</button>}
          <button type="button" className="title-action" onClick={() => open({ kind: "add" })}>＋ ユーザーを追加</button>
        </div>
        {picked.length === 0 ? (
          <p className="muted">一覧で選ぶとまとめて操作できます。</p>
        ) : (
          <div className="bulk-bar">
            <div className="bulk-summary">
              <div className="row">
                <button type="button" className="link" aria-label="選択を解除" onClick={() => setSelected(new Set())}>✕</button>
                <span>{shown.length} 人中 {picked.length} 人を選択しています。</span>
              </div>
              <div className="row bulk-links">
                <button type="button" className="link" disabled={invitees.length === 0 || progress !== null} onClick={sendInvitations}
                  title="ログイン前・期限切れのユーザーに送ります">✉ 招待メールを送る{invitees.length > 0 && `（${invitees.length} 人）`}</button>
                <button type="button" className="link danger" disabled={invitees.length === 0 || progress !== null} onClick={cancelRegistrations}
                  title="ログイン前・期限切れのユーザーの登録を取り消します">登録を取り消す{invitees.length > 0 && `（${invitees.length} 人）`}</button>
                <button type="button" className="link danger" disabled={removable.length === 0 || progress !== null} onClick={removeFromContract}
                  title="ログイン済み・無効のユーザーをこの契約から除外します">契約から除外{removable.length > 0 && `（${removable.length} 人）`}</button>
              </div>
            </div>
            <span className="spacer" />
            {rows.teams.length > 0 && (
              <Dropdown label="チームを選択" open={menu === "team"} onToggle={toggleMenu("team")}>
                <TeamPicker teams={rows.teams} users={picked} onApply={applyAndClose(applyTeams)} />
              </Dropdown>
            )}
            <Dropdown label="権限を選択" open={menu === "role"} onToggle={toggleMenu("role")}>
              <ChoicePicker<Role> name="bulk-role" options={[{ value: "admin", label: roleLabel("admin") }, { value: "member", label: roleLabel("member") }]}
                onApply={applyAndClose(changeRole)} />
            </Dropdown>
            <Dropdown label="状態を選択" open={menu === "status"} onToggle={toggleMenu("status")}>
              <ChoicePicker<"active" | "disabled"> name="bulk-status" options={[{ value: "active", label: "有効" }, { value: "disabled", label: "無効（この契約の全サービスを止める）" }]}
                onApply={applyAndClose(changeStatus)} />
            </Dropdown>
          </div>
        )}
        {progress && <p className="muted">{progress}</p>}
        {error && <p className="error" style={{ whiteSpace: "pre-line" }}>{error}</p>}
        {notice && <p className="ok">{notice}</p>}

        <table className="member-table">
          <thead>
            <tr>
              <th>
                <input type="checkbox" aria-label="表示中のユーザーをすべて選ぶ" disabled={shown.length === 0}
                  checked={shown.length > 0 && picked.length === shown.length} onChange={(e) => toggleAll(e.target.checked)} />
              </th>
              <th>メンバー</th><th>Cognito sub</th><th>チーム</th><th>権限</th><th>状態</th><th>招待メール</th><th>最終ログイン</th><th>サービス可否</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={9} className="muted">メンバーはいません。</td></tr>}
            {shown.map((u) => (
              <tr key={u.sub}>
                <td>
                  <input type="checkbox" aria-label={`${u.email} を選ぶ`} checked={selected.has(u.sub)} onChange={(e) => toggle(u.sub, e.target.checked)} />
                </td>
                <td>
                  <div className="member">
                    <span className="avatar" aria-hidden>{(u.email ?? "?").slice(0, 1).toUpperCase()}</span>
                    <div>
                      <strong>{u.email}</strong>
                    </div>
                  </div>
                </td>
                <td><SubCode sub={u.sub} /></td>
                <td>{u.teams.map((t) => <div key={t.teamId}>{t.name}</div>)}{u.teams.length === 0 && <span className="muted">—</span>}</td>
                <td>{roleLabel(u.role)}</td>
                <td><span className="badge">{displayStatus(u)}</span></td>
                <td className="muted">
                  {u.status === "invited" && u.invitedAt ? (
                    <>
                      <div>送信 {fmt(u.lastInvitedAt ?? u.invitedAt)}（{u.inviteCount ?? 1} 回）</div>
                      <div>期限 {fmt(u.expiresAt)}</div>
                    </>
                  ) : "—"}
                </td>
                <td className="muted">{fmt(u.lastLoginAt)}</td>
                <td><button onClick={() => open({ kind: "user", user: u })}>編集</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {dialog?.kind === "add" && (
        <Modal title="ユーザーを追加" wide onClose={close}>
          <AddUserPanel contractId={contractId} rows={rows} onChanged={() => void load()} />
        </Modal>
      )}
      {dialog?.kind === "user" && (
        <Modal title={`${dialog.user.email ?? "ユーザー"} のサービス可否`} onClose={close}>
          <ServiceEditor
            user={dialog.user} rows={rows} error={dialogError} onCancel={close}
            onSave={(patch) => act(() => api.updateUser(contractId, dialog.user.sub, patch))}
          />
        </Modal>
      )}
      {dialog?.kind === "team" && (
        <Modal title={dialog.team ? "チームを編集" : "チームを作成"} onClose={close}>
          <TeamEditor
            team={dialog.team} error={dialogError} onCancel={close}
            onSave={(body) => {
              const t = dialog.team;
              return act(() => (t ? api.updateTeam(contractId, t.teamId, body) : api.createTeam(contractId, { name: body.name })));
            }}
            onDelete={dialog.team ? () => { const t = dialog.team!; void act(() => api.deleteTeam(contractId, t.teamId), () => setTeamId(null)); } : undefined}
          />
        </Modal>
      )}
    </div>
  );
}
